import { useEffect, useState, useCallback } from "react";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  LayoutDashboard, Armchair, QrCode, MapPin, Timer, Users, Settings as SettingsIcon,
  ScrollText, Download, Search as SearchIcon, RotateCcw, LogOut, IndianRupee, TrendingUp,
  Wallet, Smartphone, Activity, CheckCircle2, Plus, Pencil, Trash2, Loader2, Printer, Menu, X,
  BellRing, MessageCircle, Tag, CalendarDays, Wallet as WalletIcon, Upload, Play, Pause, Copy as CopyIcon,
  Send, Megaphone, Eye, Scale, Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { SettlementManager, SettlementOverviewCard } from "./Settlement";
import { MaintenanceManager, MaintenanceOverviewCard } from "./Maintenance";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const tt = { background: "#141418", border: "1px solid #27272A", borderRadius: 8, color: "#F9F6F0" };

const NAV = [
  ["overview", "Overview", LayoutDashboard],
  ["chairs", "Chairs", Armchair],
  ["qr", "QR Codes", QrCode],
  ["locations", "Locations", MapPin],
  ["services", "Services", Timer],
  ["orders", "Orders & Search", SearchIcon],
  ["offers", "Offer Manager", Tag],
  ["festivals", "Festival Calendar", CalendarDays],
  ["payments", "Payment Settings", WalletIcon],
  ["reminders", "Return Reminders", BellRing],
  ["blasts", "WhatsApp Blast", Megaphone],
  ["users", "Team", Users],
  ["settlement", "Share Settlement", Scale],
  ["maintenance", "Maintenance", Wrench],
  ["refunds", "Refunds", RotateCcw],
  ["audit", "Audit Log", ScrollText],
  ["export", "Export", Download],
  ["settings", "Settings", SettingsIcon],
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [open, setOpen] = useState(false);
  const [maintCount, setMaintCount] = useState(0);
  useEffect(() => { api.get("/maintenance/stats").then((r) => setMaintCount(r.data.unresolved || 0)).catch(() => {}); }, [tab]);
  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-64 transform flex-col border-r border-border bg-card transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <Logo size="sm" subtitle={false} />
          <button className="lg:hidden" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
          {NAV.map(([k, label, Icon]) => (
            <button key={k} onClick={() => { setTab(k); setOpen(false); }} data-testid={`nav-${k}`}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${tab === k ? "bg-gold-muted font-semibold gold-text" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <Icon size={17} /> {label}
              {k === "maintenance" && maintCount > 0 && <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-bold text-white" data-testid="nav-maintenance-badge">{maintCount}</span>}
            </button>
          ))}
        </nav>
        <div className="w-full border-t border-border p-3">
          <div className="mb-2 px-2 text-xs text-muted-foreground">{user?.name}<br /><span className="text-gold">{user?.role?.replace("_", " ")}</span></div>
          <Button variant="outline" size="sm" onClick={doLogout} className="w-full gap-1.5 gold-border text-muted-foreground hover:text-gold"><LogOut size={15} /> Logout</Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64">
        <header className="sticky top-0 z-40 flex items-center justify-between glass border-b border-border px-4 py-3 lg:hidden">
          <button onClick={() => setOpen(true)}><Menu /></button>
          <Logo size="sm" subtitle={false} />
        </header>
        <main className="p-4 sm:p-6">
          {tab === "overview" && <Overview onNavigate={setTab} />}
          {tab === "chairs" && <Chairs />}
          {tab === "qr" && <QRCodes />}
          {tab === "locations" && <Locations />}
          {tab === "services" && <Services />}
          {tab === "orders" && <Orders />}
          {tab === "offers" && <OfferManager />}
          {tab === "festivals" && <FestivalCalendar />}
          {tab === "payments" && <PaymentSettings />}
          {tab === "reminders" && <Reminders />}
          {tab === "blasts" && <BlastManager />}
          {tab === "users" && <Team />}
          {tab === "settlement" && <SettlementManager />}
          {tab === "maintenance" && <MaintenanceManager />}
          {tab === "refunds" && <Refunds />}
          {tab === "audit" && <Audit />}
          {tab === "export" && <Export />}
          {tab === "settings" && <SettingsPanel />}
        </main>
      </div>
    </div>
  );
}

function Head({ title, sub, children }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div><div className="overline-label">{sub}</div><h1 className="mt-1 font-serif text-2xl font-bold">{title}</h1></div>
      {children}
    </div>
  );
}
function Loading() { return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin gold-text" /></div>; }

/* ---------- Overview ---------- */
function Overview({ onNavigate }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/admin").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Loading />;
  const kpis = [
    [TrendingUp, "Today's Revenue", fmt(d.today_revenue), true],
    [Activity, "Today's Sessions", d.today_sessions],
    [Wallet, "Cash Revenue", fmt(d.cash_revenue)],
    [Smartphone, "UPI Revenue", fmt(d.upi_revenue)],
    [IndianRupee, "Average Ticket", fmt(d.average_ticket)],
    [Timer, "Active Sessions", d.active_sessions],
    [CheckCircle2, "Completed Sessions", d.completed_sessions],
    [TrendingUp, "Online Revenue", fmt(d.online_revenue)],
  ];
  return (
    <div>
      <Head title="Business Overview" sub="Admin Dashboard" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map(([Icon, l, v, accent]) => (
          <div key={l} className={`rounded-2xl p-5 ${accent ? "bg-gold-muted gold-border" : "bg-card border border-border"}`} data-testid={`kpi-${l.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <Icon className="gold-text" size={20} />
            <div className="mt-3 text-xs text-muted-foreground">{l}</div>
            <div className="mt-1 text-2xl font-bold">{v}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <Stat l="7-Day Revenue" v={fmt(d.revenue_7d)} />
        <Stat l="30-Day Revenue" v={fmt(d.revenue_30d)} />
        <Stat l="Total Revenue" v={fmt(d.total_revenue)} />
      </div>
      <div className="mt-6 rounded-2xl bg-card p-6 gold-border" data-testid="eye-massager-summary">
        <div className="mb-4 flex items-center gap-2 font-serif text-lg font-semibold"><Eye size={18} className="gold-text" /> Eye Massager Add-On</div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat l="Massage Revenue" v={fmt(d.massage_revenue)} />
          <Stat l="Eye Add-On Revenue" v={fmt(d.eye_massager_revenue)} />
          <Stat l="Eye Add-Ons Sold" v={d.eye_massager_count} />
          <Stat l="Add-On Usage" v={`${d.eye_massager_usage_pct}%`} />
        </div>
        <div className="mt-3 text-xs text-muted-foreground">Today: {fmt(d.eye_massager_today_revenue)} from {d.eye_massager_today_count} add-on{d.eye_massager_today_count === 1 ? "" : "s"} · combined revenue includes the add-on automatically.</div>
      </div>
      <div className="mt-6">
        <SettlementOverviewCard onView={() => onNavigate && onNavigate("settlement")} />
      </div>
      <div className="mt-6">
        <MaintenanceOverviewCard onView={() => onNavigate && onNavigate("maintenance")} />
      </div>
      <div className="mt-6 rounded-2xl bg-card p-6 gold-border">
        <div className="mb-4 font-serif text-lg font-semibold">Revenue — Last 30 Days</div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={d.revenue_trend}>
            <defs><linearGradient id="ga" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E2B857" stopOpacity={0.4} /><stop offset="100%" stopColor="#E2B857" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
            <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fill: "#71717A", fontSize: 11 }} />
            <Tooltip contentStyle={tt} formatter={(v) => fmt(v)} />
            <Area type="monotone" dataKey="revenue" stroke="#E2B857" strokeWidth={2} fill="url(#ga)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
function Stat({ l, v }) { return <div className="rounded-2xl bg-card p-5 border border-border"><div className="text-xs text-muted-foreground">{l}</div><div className="mt-1 text-xl font-bold gold-text">{v}</div></div>; }

/* ---------- Chairs ---------- */
function Chairs() {
  const [rows, setRows] = useState(null);
  const [locs, setLocs] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/chairs").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); api.get("/admin/locations").then((r) => setLocs(r.data)); }, [load]);
  if (!rows) return <Loading />;
  const blank = { slug: "", name: "", location_id: locs[0]?.id || "", status: "available", active: true };
  const save = async (f) => {
    try {
      if (f.id) await api.put(`/admin/chairs/${f.id}`, f); else await api.post("/admin/chairs", f);
      toast.success("Chair saved"); setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/chairs/${id}`); toast.success("Chair deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <Head title="Chair Management" sub="Operations">
        <Button className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" onClick={() => setEdit(blank)} data-testid="add-chair-btn"><Plus size={16} /> Add Chair</Button>
      </Head>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((c) => (
          <div key={c.id} className="rounded-2xl bg-card p-5 gold-border" data-testid={`chair-${c.slug}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-lg font-bold gold-text">{c.slug}</span>
              <StatusBadge status={c.status} />
            </div>
            <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={12} />{c.location_name}</div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <Mini l="Sessions" v={c.total_sessions} /><Mini l="Total Rev" v={fmt(c.total_revenue)} />
              <Mini l="Today" v={fmt(c.today_revenue)} /><Mini l="Utilization" v={`${c.utilization_today}%`} />
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 gap-1 gold-border text-gold" onClick={() => setEdit(c)}><Pencil size={13} /> Edit</Button>
              <Button size="sm" variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10" onClick={() => del(c.id)}><Trash2 size={13} /></Button>
            </div>
          </div>
        ))}
      </div>
      <ChairDialog edit={edit} setEdit={setEdit} locs={locs} save={save} />
    </div>
  );
}
function ChairDialog({ edit, setEdit, locs, save }) {
  const [f, setF] = useState(edit);
  useEffect(() => setF(edit), [edit]);
  if (!f) return null;
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="bg-card">
        <DialogHeader><DialogTitle>{f.id ? "Edit" : "Add"} Chair</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Chair ID</Label><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toUpperCase() })} placeholder="MUM1-03" className="mt-1.5 font-mono" data-testid="chair-slug-input" /></div>
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Location</Label>
            <Select value={f.location_id} onValueChange={(v) => setF({ ...f, location_id: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{locs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="out_of_service">Out of Service</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={() => save(f)} className="bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="save-chair-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- QR ---------- */
function QRCodes() {
  const [chairs, setChairs] = useState(null);
  const [qr, setQr] = useState(null);
  useEffect(() => { api.get("/admin/chairs").then((r) => setChairs(r.data)); }, []);
  if (!chairs) return <Loading />;
  const show = async (c) => { const { data } = await api.get(`/admin/chairs/${c.id}/qr`); setQr(data); };
  const download = (data) => { const a = document.createElement("a"); a.href = data.qr; a.download = `KPS-${data.slug}-QR.png`; a.click(); };
  const printPoster = (data) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const slug = esc(data.slug);
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${slug} Poster</title></head><body style="font-family:Georgia,serif;text-align:center;padding:40px;background:#0B0B0E;color:#F9F6F0">
      <div style="max-width:420px;margin:auto;border:2px solid #E2B857;border-radius:24px;padding:40px 30px">
      <div style="letter-spacing:.25em;font-size:12px;color:#E2B857">KING'S PROFESSIONAL SALON</div>
      <h1 style="font-size:38px;margin:8px 0;color:#E2B857">SMART MASSAGE</h1>
      <div style="font-size:14px;color:#A1A1AA;margin-bottom:20px">Scan To Start</div>
      <img src="${esc(data.qr)}" style="width:260px;height:260px;background:#fff;padding:12px;border-radius:12px"/>
      <div style="text-align:left;margin:24px auto 0;max-width:220px;font-size:14px;line-height:1.9;color:#F9F6F0">1. Choose your massage<br/>2. Pay securely<br/>3. Show your billing code<br/>4. Relax</div>
      <div style="margin-top:24px;font-size:20px;font-weight:bold">Chair: ${slug}</div>
      <div style="font-size:12px;color:#A1A1AA">${esc(data.location_name)}</div>
      </div><script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  };
  return (
    <div>
      <Head title="QR Code Management" sub="Every Chair, One QR" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {chairs.map((c) => (
          <div key={c.id} className="rounded-2xl bg-card p-5 gold-border text-center" data-testid={`qr-chair-${c.slug}`}>
            <div className="font-mono text-lg font-bold gold-text">{c.slug}</div>
            <div className="text-xs text-muted-foreground">{c.location_name}</div>
            <div className="mt-2 break-all rounded bg-secondary/50 p-2 text-[10px] text-muted-foreground">{c.qr_url}</div>
            <Button size="sm" className="mt-3 w-full gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" onClick={() => show(c)} data-testid={`view-qr-${c.slug}`}><QrCode size={14} /> View QR</Button>
          </div>
        ))}
      </div>
      <Dialog open={!!qr} onOpenChange={(o) => !o && setQr(null)}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle>QR — {qr?.slug}</DialogTitle></DialogHeader>
          {qr && (
            <div className="text-center">
              <img src={qr.qr} alt="QR" className="mx-auto h-56 w-56 rounded-lg bg-white p-2" />
              <div className="mt-2 break-all text-xs text-muted-foreground">{qr.qr_url}</div>
              <div className="mt-4 flex gap-2">
                <Button className="flex-1 gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" onClick={() => download(qr)} data-testid="download-qr-btn"><Download size={15} /> Download</Button>
                <Button variant="outline" className="flex-1 gap-1.5 gold-border text-gold" onClick={() => printPoster(qr)} data-testid="print-poster-btn"><Printer size={15} /> Print Poster</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Locations ---------- */
function Locations() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/locations").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loading />;
  const save = async (f) => { try { if (f.id) await api.put(`/admin/locations/${f.id}`, f); else await api.post("/admin/locations", f); toast.success("Saved"); setEdit(null); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { try { await api.delete(`/admin/locations/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <Head title="Locations" sub="Salon Network">
        <Button className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" onClick={() => setEdit({ code: "", name: "", address: "", active: true })} data-testid="add-location-btn"><Plus size={16} /> Add Location</Button>
      </Head>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((l) => (
          <div key={l.id} className="rounded-2xl bg-card p-5 gold-border" data-testid={`location-${l.code}`}>
            <div className="flex items-center justify-between"><span className="font-mono font-bold gold-text">{l.code}</span></div>
            <div className="mt-2 font-serif text-lg font-semibold">{l.name}</div>
            <div className="text-xs text-muted-foreground">{l.address}</div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 gap-1 gold-border text-gold" onClick={() => setEdit(l)}><Pencil size={13} /> Edit</Button>
              <Button size="sm" variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10" onClick={() => del(l.id)}><Trash2 size={13} /></Button>
            </div>
          </div>
        ))}
      </div>
      <FormDialog edit={edit} setEdit={setEdit} title="Location" save={save} fields={[["code", "Code"], ["name", "Name"], ["address", "Address"]]} />
    </div>
  );
}

/* ---------- Services ---------- */
function Services() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/services").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loading />;
  const save = async (f) => {
    const body = {
      ...f,
      duration_minutes: Number(f.duration_minutes),
      price: Number(f.price),
      sort_order: Number(f.sort_order || 0),
      offer_active: !!f.offer_active,
      offer_price: f.offer_price === "" || f.offer_price == null ? null : Number(f.offer_price),
      offer_starts_at: f.offer_starts_at || null,
      offer_ends_at: f.offer_ends_at || null,
    };
    try { if (f.id) await api.put(`/admin/services/${f.id}`, body); else await api.post("/admin/services", body); toast.success("Saved"); setEdit(null); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/services/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <Head title="Services & Pricing" sub="Configurable">
        <Button className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" onClick={() => setEdit({ name: "", duration_minutes: 10, price: 100, active: true, sort_order: rows.length, offer_active: false, offer_price: "", offer_starts_at: "", offer_ends_at: "" })} data-testid="add-service-btn"><Plus size={16} /> Add Service</Button>
      </Head>
      <div className="overflow-x-auto rounded-2xl bg-card p-2 gold-border">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-3">Name</th><th>Duration</th><th>Price</th><th>Offer</th><th>Status</th><th className="text-right p-3">Actions</th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-border/50" data-testid={`service-row-${s.duration_minutes}`}>
                <td className="p-3 font-medium">{s.name}</td>
                <td>{s.duration_minutes} min</td>
                <td className="gold-text font-semibold">
                  {fmt(s.effective_price ?? s.price)}
                  {s.on_offer && <span className="ml-1.5 text-xs text-muted-foreground line-through">{fmt(s.original_price)}</span>}
                </td>
                <td className="text-xs">
                  {s.offer_price != null && s.offer_active ? (
                    <span className={s.on_offer ? "text-emerald-400" : "text-amber-400"}>
                      {fmt(s.offer_price)} {s.on_offer ? "· live" : "· scheduled"}
                      {s.offer_ends_at && <span className="block text-muted-foreground">till {new Date(s.offer_ends_at).toLocaleString("en-IN")}</span>}
                    </span>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td>{s.active ? <span className="text-emerald-400">Active</span> : <span className="text-muted-foreground">Inactive</span>}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" className="mr-2 gap-1 gold-border text-gold" onClick={() => setEdit({ ...s, offer_price: s.offer_price ?? "", offer_starts_at: s.offer_starts_at || "", offer_ends_at: s.offer_ends_at || "" })}><Pencil size={13} /></Button>
                  <Button size="sm" variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10" onClick={() => del(s.id)}><Trash2 size={13} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ServiceDialog edit={edit} setEdit={setEdit} save={save} />
    </div>
  );
}

function ServiceDialog({ edit, setEdit, save }) {
  const [f, setF] = useState(edit);
  useEffect(() => setF(edit), [edit]);
  if (!f) return null;
  const set = (k, v) => setF({ ...f, [k]: v });
  const toLocal = (v) => (v ? v.slice(0, 16) : "");
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{f.id ? "Edit" : "Add"} Service</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="field-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Duration (min)</Label><Input type="number" value={f.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className="mt-1.5" data-testid="field-duration_minutes" /></div>
            <div><Label>Base Price (₹)</Label><Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="mt-1.5" data-testid="field-price" /></div>
          </div>
          <div><Label>Sort Order</Label><Input type="number" value={f.sort_order} onChange={(e) => set("sort_order", e.target.value)} className="mt-1.5" /></div>
          <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} /><Label>Active</Label></div>

          <div className="mt-2 rounded-lg border border-border bg-secondary/30 p-3">
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-gold">Scheduled Offer Price</Label>
              <div className="flex items-center gap-2"><Switch checked={!!f.offer_active} onCheckedChange={(v) => set("offer_active", v)} data-testid="offer-active-switch" /><span className="text-xs text-muted-foreground">Enable</span></div>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">The offer price auto-applies during the window below and reverts to base price on its own.</p>
            <div className="grid grid-cols-1 gap-3">
              <div><Label>Offer Price (₹)</Label><Input type="number" value={f.offer_price} onChange={(e) => set("offer_price", e.target.value)} className="mt-1.5" placeholder="e.g. 180" data-testid="field-offer_price" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Starts</Label><Input type="datetime-local" value={toLocal(f.offer_starts_at)} onChange={(e) => set("offer_starts_at", e.target.value)} className="mt-1.5" data-testid="field-offer_starts_at" /></div>
                <div><Label>Ends</Label><Input type="datetime-local" value={toLocal(f.offer_ends_at)} onChange={(e) => set("offer_ends_at", e.target.value)} className="mt-1.5" data-testid="field-offer_ends_at" /></div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={() => save(f)} className="bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="save-service-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Orders & Search ---------- */
function Orders() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const load = useCallback(() => api.get("/admin/orders").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const search = async () => { if (!q.trim()) return load(); const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q)}`); setRows(data); };
  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/orders/${delTarget.id}`);
      toast.success(`Order ${delTarget.order_number || ""} deleted`);
      setDelTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeleting(false); }
  };
  if (!rows) return <Loading />;
  return (
    <div>
      <Head title="Orders & Search" sub="Transactions" />
      <div className="mb-4 flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search name, mobile, billing code, chair…" data-testid="search-input" />
        <Button onClick={search} className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="search-btn"><SearchIcon size={15} /> Search</Button>
      </div>
      {isSuper && <p className="mb-3 text-xs text-muted-foreground">Signed in as <span className="text-gold">super admin</span> — you can permanently delete test / junk orders. Deleting also removes the linked session, billing code and one-off customer record, and is written to the audit log.</p>}
      <div className="overflow-x-auto rounded-2xl bg-card p-2 gold-border">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-3">Date</th><th>Customer</th><th>Chair</th><th>Service</th><th>Eye</th><th>Amount</th><th>Pay</th><th>Code</th><th>Status</th>{isSuper && <th className="p-3 text-right">Action</th>}</tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} className="border-b border-border/50" data-testid={`order-row-${o.id}`}>
                <td className="p-3 whitespace-nowrap text-xs text-muted-foreground">{new Date(o.confirmed_at || o.created_at).toLocaleString("en-IN")}</td>
                <td>{o.customer_name}{o.is_demo && <span className="ml-1 rounded bg-secondary px-1 text-[9px] text-gold">DEMO</span>}</td>
                <td className="font-mono text-xs">{o.chair_code}</td>
                <td className="text-xs">{o.duration_minutes}m</td>
                <td className="text-xs">{o.eye_massager_selected ? <span className="font-semibold text-emerald-400">YES</span> : <span className="text-muted-foreground">—</span>}</td>
                <td className="gold-text font-semibold">{fmt(o.amount)}</td>
                <td className="text-xs uppercase">{o.payment_method}</td>
                <td className="font-mono text-xs">{o.billing_code || "—"}</td>
                <td><span className={`text-xs ${o.status === "paid" ? "text-emerald-400" : o.status === "refunded" ? "text-destructive" : "text-muted-foreground"}`}>{o.status}</span></td>
                {isSuper && <td className="p-3 text-right"><Button size="icon" variant="ghost" onClick={() => setDelTarget(o)} className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`delete-order-${o.id}`}><Trash2 size={15} /></Button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle>Delete this order permanently?</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">
            This permanently removes order <span className="font-mono text-gold">{delTarget?.order_number}</span> for <b>{delTarget?.customer_name}</b> ({fmt(delTarget?.amount || 0)}), along with its session, billing code and one-off customer record. This cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)} data-testid="cancel-delete-btn">Cancel</Button>
            <Button onClick={doDelete} disabled={deleting} className="gap-1.5 bg-destructive text-white hover:bg-destructive/90" data-testid="confirm-delete-order-btn">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 size={15} /> Delete Order</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Offer Manager ---------- */
const OFFER_TYPES = ["percentage", "coupon", "bogo", "seasonal", "time", "weekend", "package", "combo"];
const OFFER_STATUSES = ["draft", "pending", "scheduled", "active", "paused", "expired", "cancelled", "rejected"];
function OfferManager() {
  const [rows, setRows] = useState(null);
  const [locs, setLocs] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/offers").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); api.get("/admin/locations").then((r) => setLocs(r.data)); }, [load]);
  if (!rows) return <Loading />;
  const setStatus = async (o, status) => { try { await api.post(`/admin/offers/${o.id}/status`, { status }); toast.success(`Offer ${status}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const dup = async (o) => { try { await api.post(`/admin/offers/${o.id}/duplicate`); toast.success("Duplicated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (o) => { try { await api.delete(`/admin/offers/${o.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  const gen = async () => { setBusy(true); try { const { data } = await api.post("/admin/offers/generate"); toast.success(`${data.created} draft(s) generated`); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const save = async (f) => {
    const body = { ...f, discount_percent: Number(f.discount_percent || 0),
      location_ids: f.location_ids || [], service_durations: (f.service_durations || []).map(Number),
      coupon_code: f.coupon_code || null,
      time_start_hour: f.time_start_hour === "" ? null : (f.time_start_hour ?? null),
      time_end_hour: f.time_end_hour === "" ? null : (f.time_end_hour ?? null) };
    try { if (f.id) await api.put(`/admin/offers/${f.id}`, body); else await api.post("/admin/offers", body); toast.success("Saved"); setEdit(null); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const blank = { name: "", type: "percentage", discount_percent: 20, coupon_code: "", location_ids: [], service_durations: [], status: "draft", category: "Custom", description: "", starts_at: "", ends_at: "", time_start_hour: "", time_end_hour: "" };
  return (
    <div>
      <Head title="Offer Manager" sub="Promotions">
        <div className="flex gap-2">
          <Button variant="outline" onClick={gen} disabled={busy} className="gap-1.5 gold-border text-gold" data-testid="generate-offers-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CalendarDays size={15} /> Generate Festival Drafts</>}</Button>
          <Button onClick={() => setEdit(blank)} className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="add-offer-btn"><Plus size={16} /> New Offer</Button>
        </div>
      </Head>
      <div className="grid gap-4 lg:grid-cols-2">
        {rows.map((o) => (
          <div key={o.id} className="rounded-2xl bg-card p-5 gold-border" data-testid={`offer-${o.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-serif text-lg font-semibold">{o.name}</div>
                <div className="mt-0.5 flex items-center gap-2 text-xs">
                  <span className="rounded bg-secondary px-2 py-0.5 uppercase">{o.type}</span>
                  {o.category && <span className="text-muted-foreground">{o.category}</span>}
                  {o.is_live && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-400">LIVE</span>}
                </div>
              </div>
              <StatusPill status={o.status} />
            </div>
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
              {o.type !== "bogo" && <span className="gold-text font-semibold">{o.discount_percent}% OFF</span>}
              {o.coupon_code && <span className="font-mono">Code: {o.coupon_code}</span>}
              <span>{o.starts_at ? new Date(o.starts_at).toLocaleDateString("en-IN") : "—"} → {o.ends_at ? new Date(o.ends_at).toLocaleDateString("en-IN") : "—"}</span>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
              <Mini l="Views" v={o.views} /><Mini l="Claims" v={o.claims} /><Mini l="Sales" v={o.purchases} /><Mini l="Revenue" v={fmt(o.revenue)} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {["draft", "pending", "rejected", "cancelled"].includes(o.status) && <Button size="sm" onClick={() => setStatus(o, "active")} className="gap-1 bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`approve-${o.id}`}><CheckCircle2 size={13} /> Approve</Button>}
              {o.status === "active" && <Button size="sm" variant="outline" onClick={() => setStatus(o, "paused")} className="gap-1 gold-border text-gold" data-testid={`pause-${o.id}`}><Pause size={13} /> Pause</Button>}
              {o.status === "paused" && <Button size="sm" onClick={() => setStatus(o, "active")} className="gap-1 bg-emerald-600 text-white hover:bg-emerald-700"><Play size={13} /> Resume</Button>}
              <Button size="sm" variant="outline" className="gap-1 gold-border text-gold" onClick={() => setEdit({ ...o, starts_at: (o.starts_at || "").slice(0, 16), ends_at: (o.ends_at || "").slice(0, 16), time_start_hour: o.time_start_hour ?? "", time_end_hour: o.time_end_hour ?? "" })}><Pencil size={13} /></Button>
              <Button size="sm" variant="outline" className="gap-1 gold-border text-gold" onClick={() => dup(o)}><CopyIcon size={13} /></Button>
              <Button size="sm" variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10" onClick={() => del(o)}><Trash2 size={13} /></Button>
            </div>
          </div>
        ))}
      </div>
      <OfferDialog edit={edit} setEdit={setEdit} save={save} locs={locs} />
    </div>
  );
}
function StatusPill({ status }) {
  const map = { active: "bg-emerald-500/15 text-emerald-400", draft: "bg-secondary text-muted-foreground", paused: "bg-amber-500/15 text-amber-400", scheduled: "bg-blue-500/15 text-blue-400", expired: "bg-secondary text-muted-foreground", rejected: "bg-destructive/15 text-destructive", cancelled: "bg-destructive/15 text-destructive", pending: "bg-amber-500/15 text-amber-400" };
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[status] || "bg-secondary"}`}>{status}</span>;
}
function OfferDialog({ edit, setEdit, save, locs }) {
  const [f, setF] = useState(edit);
  useEffect(() => setF(edit), [edit]);
  if (!f) return null;
  const set = (k, v) => setF({ ...f, [k]: v });
  const toggleLoc = (id) => set("location_ids", (f.location_ids || []).includes(id) ? f.location_ids.filter((x) => x !== id) : [...(f.location_ids || []), id]);
  const toggleDur = (d) => set("service_durations", (f.service_durations || []).includes(d) ? f.service_durations.filter((x) => x !== d) : [...(f.service_durations || []), d]);
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{f.id ? "Edit" : "New"} Offer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="offer-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={f.type} onValueChange={(v) => set("type", v)}><SelectTrigger className="mt-1.5" data-testid="offer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{OFFER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Discount %</Label><Input type="number" value={f.discount_percent} onChange={(e) => set("discount_percent", e.target.value)} className="mt-1.5" data-testid="offer-discount-input" disabled={f.type === "bogo"} /></div>
          </div>
          {f.type === "coupon" && <div><Label>Coupon Code</Label><Input value={f.coupon_code || ""} onChange={(e) => set("coupon_code", e.target.value.toUpperCase())} className="mt-1.5 font-mono" placeholder="FESTIVE30" data-testid="offer-coupon-input" /></div>}
          {f.type === "time" && <div className="grid grid-cols-2 gap-3"><div><Label>Start Hour (0-23)</Label><Input type="number" value={f.time_start_hour} onChange={(e) => set("time_start_hour", e.target.value === "" ? "" : Number(e.target.value))} className="mt-1.5" /></div><div><Label>End Hour</Label><Input type="number" value={f.time_end_hour} onChange={(e) => set("time_end_hour", e.target.value === "" ? "" : Number(e.target.value))} className="mt-1.5" /></div></div>}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Starts</Label><Input type="datetime-local" value={f.starts_at || ""} onChange={(e) => set("starts_at", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Ends</Label><Input type="datetime-local" value={f.ends_at || ""} onChange={(e) => set("ends_at", e.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label>Locations <span className="text-muted-foreground">(none = all)</span></Label>
            <div className="mt-1.5 flex flex-wrap gap-2">{locs.map((l) => <button key={l.id} type="button" onClick={() => toggleLoc(l.id)} className={`rounded-full border px-3 py-1 text-xs ${(f.location_ids || []).includes(l.id) ? "border-gold bg-gold-muted text-gold" : "border-border text-muted-foreground"}`}>{l.name}</button>)}</div>
          </div>
          <div><Label>Durations <span className="text-muted-foreground">(none = all)</span></Label>
            <div className="mt-1.5 flex gap-2">{[10, 20, 30].map((d) => <button key={d} type="button" onClick={() => toggleDur(d)} className={`rounded-full border px-3 py-1 text-xs ${(f.service_durations || []).includes(d) ? "border-gold bg-gold-muted text-gold" : "border-border text-muted-foreground"}`}>{d} min</button>)}</div>
          </div>
          <div><Label>Description</Label><Input value={f.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button onClick={() => save(f)} className="bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="save-offer-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Festival Calendar ---------- */
function FestivalCalendar() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/festival-calendar").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loading />;
  const gen = async () => { setBusy(true); try { const { data } = await api.post("/admin/offers/generate"); toast.success(`${data.created} draft(s) created`); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const approve = async (r) => { if (!r.offer_id) { toast.info("Generate the draft first"); return; } try { await api.post(`/admin/offers/${r.offer_id}/status`, { status: "active" }); toast.success("Offer approved & live"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <Head title="Festival & Occasion Calendar" sub="India-wide, all communities">
        <Button onClick={gen} disabled={busy} className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="cal-generate-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CalendarDays size={15} /> Generate 3-Day Drafts</>}</Button>
      </Head>
      <div className="overflow-x-auto rounded-2xl bg-card p-2 gold-border">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-3">Occasion</th><th>Category</th><th>Date</th><th>In</th><th>Offer</th><th className="text-right p-3">Action</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.festival_key} className="border-b border-border/50" data-testid={`festival-${r.festival_key}`}>
                <td className="p-3 font-medium">{r.name}</td>
                <td><span className="rounded bg-secondary px-2 py-0.5 text-xs">{r.category}</span></td>
                <td className="text-xs">{new Date(r.date).toLocaleDateString("en-IN")}</td>
                <td className="text-xs text-muted-foreground">{r.days_away}d</td>
                <td><StatusPill status={r.offer_status === "not_created" ? "draft" : r.offer_status} /></td>
                <td className="p-3 text-right">
                  {["draft", "pending", "not_created"].includes(r.offer_status)
                    ? <Button size="sm" onClick={() => approve(r)} disabled={!r.offer_id} className="gap-1 bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`festival-approve-${r.festival_key}`}><CheckCircle2 size={13} /> Approve</Button>
                    : <span className="text-xs text-emerald-400">Published</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Payment Settings (per location UPI/GPay) ---------- */
function PaymentSettings() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/locations").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loading />;
  const save = async (l) => { try { await api.put(`/admin/locations/${l.id}`, l); toast.success("Payment settings saved"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <Head title="Location Payment Settings" sub="Per-Location GPay / UPI" />
      <div className="grid gap-5 lg:grid-cols-2">
        {rows.map((l) => <LocationPayCard key={l.id} loc={l} save={save} />)}
      </div>
    </div>
  );
}
function LocationPayCard({ loc, save }) {
  const [f, setF] = useState(loc);
  useEffect(() => setF(loc), [loc]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const upload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("upi_qr", reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="rounded-2xl bg-card p-6 gold-border" data-testid={`paycard-${f.code}`}>
      <div className="flex items-center justify-between">
        <div className="font-serif text-lg font-semibold">{f.name} <span className="ml-1 font-mono text-xs gold-text">{f.code}</span></div>
        <div className="flex items-center gap-2"><Switch checked={f.payment_active} onCheckedChange={(v) => set("payment_active", v)} /><span className="text-xs text-muted-foreground">Active</span></div>
      </div>
      <div className="mt-4 space-y-3">
        <div><Label>UPI ID</Label><Input value={f.upi_id || ""} onChange={(e) => set("upi_id", e.target.value)} className="mt-1.5 font-mono" data-testid={`upi-id-${f.code}`} /></div>
        <div><Label>UPI Display Name</Label><Input value={f.upi_name || ""} onChange={(e) => set("upi_name", e.target.value)} className="mt-1.5" data-testid={`upi-name-${f.code}`} /></div>
        <div><Label>Payment Instructions</Label><Input value={f.payment_instructions || ""} onChange={(e) => set("payment_instructions", e.target.value)} className="mt-1.5" /></div>
        <div>
          <Label>GPay / UPI QR Image</Label>
          <div className="mt-1.5 flex items-center gap-3">
            {f.upi_qr ? <img src={f.upi_qr} alt="QR" className="h-20 w-20 rounded bg-white p-1" data-testid={`qr-preview-${f.code}`} /> : <div className="flex h-20 w-20 items-center justify-center rounded border border-dashed border-border text-[10px] text-muted-foreground">No QR</div>}
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid={`qr-upload-${f.code}`} />
                <span className="inline-flex items-center gap-1.5 rounded-md border border-gold/40 px-3 py-1.5 text-xs text-gold hover:bg-gold-muted"><Upload size={13} /> {f.upi_qr ? "Replace QR" : "Upload QR"}</span>
              </label>
              {f.upi_qr && <button onClick={() => set("upi_qr", null)} className="text-xs text-destructive">Remove</button>}
            </div>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Leave empty to auto-generate a UPI QR with the exact amount. Upload a static GPay QR for manual (staff-confirmed) payments.</p>
        </div>
      </div>
      <Button onClick={() => save(f)} className="mt-4 w-full bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid={`save-pay-${f.code}`}>Save {f.code}</Button>
    </div>
  );
}

/* ---------- Return Reminders ---------- */
function Reminders() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/reminders").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loading />;
  const scan = async () => { setBusy(true); try { const { data } = await api.post("/admin/reminders/scan"); toast.success(`${data.created} new reminder(s) found`); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const markSent = async (r) => { try { await api.post(`/admin/reminders/${r.id}/mark-sent`); toast.success("Marked as sent"); load(); } catch (e) { toast.error(apiError(e)); } };
  const waHref = (r) => { const d = (r.customer_mobile || "").replace(/\D/g, ""); const n = d.length === 10 ? `91${d}` : d; return `https://wa.me/${n}?text=${encodeURIComponent(r.message || "")}`; };
  const pending = rows.filter((r) => r.status === "pending").length;
  return (
    <div>
      <Head title="Return Reminders" sub="Win-Back">
        <Button onClick={scan} disabled={busy} className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="scan-reminders-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><BellRing size={15} /> Scan Now</>}
        </Button>
      </Head>
      <div className="mb-4 rounded-xl bg-secondary/40 p-4 text-sm text-muted-foreground">
        A week after a customer's first visit (if they haven't returned), a gentle "come back and relax" nudge is queued here automatically each day. Tap WhatsApp to send it, then mark it done. <span className="text-gold">{pending} pending</span>.
      </div>
      {rows.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-border py-20 text-center">
          <BellRing className="text-muted-foreground/40" size={40} />
          <p className="mt-3 text-sm text-muted-foreground">No reminders yet. They appear a week after a first visit.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl bg-card p-2 gold-border">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-3">Customer</th><th>Mobile</th><th>First Visit</th><th>Service</th><th>Status</th><th className="text-right p-3">Send</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border/50" data-testid={`reminder-${r.id}`}>
                  <td className="p-3 font-medium">{r.customer_name}{r.is_demo && <span className="ml-1 rounded bg-secondary px-1 text-[9px] text-gold">DEMO</span>}</td>
                  <td className="font-mono text-xs">{r.customer_mobile}</td>
                  <td className="text-xs text-muted-foreground">{new Date(r.first_visit_at).toLocaleDateString("en-IN")}</td>
                  <td className="text-xs">{r.first_service}</td>
                  <td>{r.status === "sent" ? <span className="text-emerald-400 text-xs">Sent</span> : <span className="text-amber-400 text-xs">Pending</span>}</td>
                  <td className="p-3 text-right">
                    <a href={waHref(r)} target="_blank" rel="noreferrer" className="mr-2 inline-block">
                      <Button size="sm" variant="outline" className="gap-1 gold-border text-gold" data-testid={`reminder-wa-${r.id}`}><MessageCircle size={13} /> WhatsApp</Button>
                    </a>
                    {r.status !== "sent" && <Button size="sm" variant="outline" className="gap-1 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" onClick={() => markSent(r)} data-testid={`reminder-sent-${r.id}`}><CheckCircle2 size={13} /> Done</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- WhatsApp Blast ---------- */
function BlastPill({ status }) {
  const map = { draft: "bg-secondary text-muted-foreground", ready: "bg-blue-500/15 text-blue-400", sent: "bg-emerald-500/15 text-emerald-400", partial: "bg-amber-500/15 text-amber-400", scheduled: "bg-indigo-500/15 text-indigo-300", cancelled: "bg-destructive/15 text-destructive" };
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[status] || "bg-secondary"}`}>{status}</span>;
}

function BlastManager() {
  const [blasts, setBlasts] = useState(null);
  const [offers, setOffers] = useState([]);
  const [tw, setTw] = useState(false);
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);
  const [schedAt, setSchedAt] = useState("");
  const [detail, setDetail] = useState(null);
  const [tplEdit, setTplEdit] = useState(null);

  const load = useCallback(() => api.get("/admin/blasts").then((r) => setBlasts(r.data)), []);
  useEffect(() => {
    load();
    api.get("/admin/offers").then((r) => setOffers(r.data.filter((o) => o.status === "active" || o.status === "scheduled")));
    api.get("/admin/blasts/template").then((r) => setTw(r.data.twilio_connected));
  }, [load]);
  if (!blasts) return <Loading />;

  const openDetail = async (b) => { const { data } = await api.get(`/admin/blasts/${b.id}`); setDetail(data); };
  const prepare = async () => {
    if (!sel) { toast.info("Pick an approved (live) offer first"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/admin/offers/${sel}/blast`, {}); toast.success(`Blast prepared — ${data.eligible} eligible customer(s)`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const schedule = async () => {
    if (!sel) { toast.info("Pick an approved (live) offer first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/offers/${sel}/schedule-blast`, schedAt ? { scheduled_at: schedAt } : {});
      toast.success(`Blast scheduled for ${new Date(data.scheduled_at).toLocaleString("en-IN")}`);
      setSchedAt(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const send = async (b) => {
    try {
      const { data } = await api.post(`/admin/blasts/${b.id}/send`);
      if (data.mode === "twilio") toast.success(`Sent ${data.sent} message(s) via WhatsApp`);
      else toast.success(`List ready — ${data.eligible} to send manually`);
      load(); if (detail?.id === b.id) openDetail(b);
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (b) => { try { await api.delete(`/admin/blasts/${b.id}`); toast.success("Deleted"); load(); if (detail?.id === b.id) setDetail(null); } catch (e) { toast.error(apiError(e)); } };
  const saveTpl = async (msg) => { try { await api.put(`/admin/blasts/${tplEdit.id}`, { message: msg }); toast.success("Message updated"); const id = tplEdit.id; setTplEdit(null); load(); if (detail?.id === id) openDetail({ id }); } catch (e) { toast.error(apiError(e)); } };
  const waHref = (r) => { const d = (r.mobile || "").replace(/\D/g, ""); const n = d.length === 10 ? `91${d}` : d; return `https://wa.me/${n}?text=${encodeURIComponent(r.message || "")}`; };
  const markSent = async (b, mobile) => { try { await api.post(`/admin/blasts/${b.id}/recipient-sent`, { mobile }); load(); openDetail(b); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <Head title="WhatsApp Blast" sub="Festival Broadcast" />
      <div className={`mb-4 flex items-start gap-3 rounded-xl p-4 text-sm ${tw ? "bg-emerald-500/10 text-emerald-300" : "bg-secondary/40 text-muted-foreground"}`} data-testid="blast-twilio-banner">
        <MessageCircle className="mt-0.5 shrink-0" size={18} />
        <div>
          {tw
            ? <span>Twilio is connected. Pressing <b>Send Blast</b> delivers the offer to every eligible past customer automatically.</span>
            : <span><b>Dry-run mode:</b> Twilio isn't connected yet, so nothing sends automatically. Prepare a blast to build the recipient queue, then open each WhatsApp link and mark it done. Connect Twilio later to enable one-tap auto-send. Anyone messaged in the last 7 days is skipped automatically.</span>}
        </div>
      </div>

      <div className="mb-5 flex flex-col gap-3 rounded-2xl bg-card p-5 gold-border">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label>Approved offer to broadcast</Label>
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="mt-1.5" data-testid="blast-offer-select"><SelectValue placeholder="Select a live offer…" /></SelectTrigger>
              <SelectContent>{offers.length === 0 ? <div className="px-3 py-2 text-xs text-muted-foreground">No live offers. Approve an offer first.</div> : offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} — {o.discount_percent}% off</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={prepare} disabled={busy} className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="prepare-blast-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Megaphone size={16} /> Prepare Now</>}
          </Button>
        </div>
        <div className="flex flex-col gap-3 border-t border-border/50 pt-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label>Or schedule it <span className="text-muted-foreground">(auto-fires when Twilio is connected)</span></Label>
            <Input type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} className="mt-1.5" data-testid="blast-schedule-input" />
            <p className="mt-1 text-[11px] text-muted-foreground">Leave blank to use the offer's start date at 10:00 AM IST. Time is read as IST.</p>
          </div>
          <Button onClick={schedule} disabled={busy} variant="outline" className="gap-1.5 gold-border text-gold" data-testid="schedule-blast-btn">
            <CalendarDays size={16} /> Schedule
          </Button>
        </div>
      </div>

      {blasts.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-border py-20 text-center">
          <Megaphone className="text-muted-foreground/40" size={40} />
          <p className="mt-3 text-sm text-muted-foreground">No blasts yet. Pick a live offer above and prepare one.</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {blasts.map((b) => (
            <div key={b.id} className="rounded-2xl bg-card p-5 gold-border" data-testid={`blast-${b.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-serif text-lg font-semibold">{b.offer_name}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{b.discount_percent}% OFF · {new Date(b.created_at).toLocaleDateString("en-IN")}</div>
                </div>
                <BlastPill status={b.status} />
              </div>
              <p className="mt-3 rounded-lg bg-secondary/40 p-3 text-xs text-muted-foreground line-clamp-3">{b.message_template}</p>
              {b.status === "scheduled" && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-indigo-500/10 p-3 text-xs text-indigo-300" data-testid={`blast-sched-note-${b.id}`}>
                  <CalendarDays size={14} className="mt-0.5 shrink-0" />
                  <span>Auto-fires <b>{new Date(b.scheduled_at).toLocaleString("en-IN")}</b>. {b.twilio_connected ? "Will send automatically via WhatsApp." : "Waiting for Twilio — nothing sends until it's connected."}</span>
                </div>
              )}
              {b.status !== "scheduled" && (
                <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
                  <Mini l="Total" v={b.total} /><Mini l="Eligible" v={b.eligible} /><Mini l="Sent" v={b.sent} /><Mini l="Skipped" v={b.skipped} />
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                {b.eligible > 0 && b.status !== "sent" && b.status !== "scheduled" && (
                  <Button size="sm" onClick={() => send(b)} className="gap-1 bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`send-blast-${b.id}`}><Send size={13} /> Send Blast</Button>
                )}
                {b.total > 0 && <Button size="sm" variant="outline" className="gap-1 gold-border text-gold" onClick={() => openDetail(b)} data-testid={`view-blast-${b.id}`}><Eye size={13} /> Recipients</Button>}
                {b.status !== "sent" && <Button size="sm" variant="outline" className="gap-1 gold-border text-gold" onClick={() => setTplEdit(b)} data-testid={`edit-blast-${b.id}`}><Pencil size={13} /> Message</Button>}
                <Button size="sm" variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10" onClick={() => del(b)} data-testid={`delete-blast-${b.id}`}>{b.status === "scheduled" ? <><X size={13} /> Cancel</> : <Trash2 size={13} />}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recipient detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-card max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.offer_name} — Recipients</DialogTitle></DialogHeader>
          {detail && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-2">Customer</th><th>Mobile</th><th>Status</th><th className="text-right p-2">Send</th></tr></thead>
                <tbody>
                  {(detail.recipients || []).map((r) => (
                    <tr key={r.mobile} className="border-b border-border/50" data-testid={`blast-recipient-${r.mobile}`}>
                      <td className="p-2 font-medium">{r.name || "—"}</td>
                      <td className="font-mono text-xs">{r.mobile}</td>
                      <td className="text-xs">
                        {r.status === "sent" ? <span className="text-emerald-400">Sent</span>
                          : r.status === "skipped" ? <span className="text-muted-foreground" title={r.skip_reason}>Skipped</span>
                          : <span className="text-amber-400">Pending</span>}
                      </td>
                      <td className="p-2 text-right">
                        {r.status === "pending" && (
                          <div className="flex justify-end gap-1.5">
                            <a href={waHref(r)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="gap-1 gold-border text-gold" data-testid={`blast-wa-${r.mobile}`}><MessageCircle size={12} /> WhatsApp</Button></a>
                            <Button size="sm" variant="outline" className="gap-1 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" onClick={() => markSent(detail, r.mobile)} data-testid={`blast-done-${r.mobile}`}><CheckCircle2 size={12} /></Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Template edit */}
      <BlastTemplateDialog tplEdit={tplEdit} setTplEdit={setTplEdit} save={saveTpl} />
    </div>
  );
}

function BlastTemplateDialog({ tplEdit, setTplEdit, save }) {
  const [msg, setMsg] = useState("");
  useEffect(() => setMsg(tplEdit?.message_template || ""), [tplEdit]);
  if (!tplEdit) return null;
  return (
    <Dialog open={!!tplEdit} onOpenChange={(o) => !o && setTplEdit(null)}>
      <DialogContent className="bg-card">
        <DialogHeader><DialogTitle>Edit Blast Message</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Message</Label>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={5}
            className="w-full rounded-lg border border-border bg-background p-3 text-sm" data-testid="blast-message-input" />
          <p className="text-xs text-muted-foreground">Use <span className="font-mono gold-text">{"{name}"}</span>, <span className="font-mono gold-text">{"{offer}"}</span> and <span className="font-mono gold-text">{"{discount}"}</span> — they fill in for each customer.</p>
        </div>
        <DialogFooter><Button onClick={() => save(msg)} className="bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="save-blast-message-btn">Save Message</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Team ---------- */
function Team() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loading />;
  const save = async (f) => { try { if (f.id) await api.put(`/admin/users/${f.id}`, f); else await api.post("/admin/users", f); toast.success("Saved"); setEdit(null); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <Head title="Team & Roles" sub="Access Control">
        <Button className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" onClick={() => setEdit({ name: "", email: "", role: "staff", password: "", active: true })} data-testid="add-user-btn"><Plus size={16} /> Add User</Button>
      </Head>
      <div className="rounded-2xl bg-card p-2 gold-border">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-3">Name</th><th>Email</th><th>Role</th><th>Status</th><th className="text-right p-3">Actions</th></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-b border-border/50">
                <td className="p-3 font-medium">{u.name}</td><td className="text-xs">{u.email}</td>
                <td><span className="rounded-full bg-gold-muted px-2 py-0.5 text-xs gold-text">{u.role.replace("_", " ")}</span></td>
                <td>{u.active ? <span className="text-emerald-400 text-xs">Active</span> : <span className="text-muted-foreground text-xs">Inactive</span>}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" className="mr-2 gold-border text-gold" onClick={() => setEdit({ ...u, password: "" })}><Pencil size={13} /></Button>
                  <Button size="sm" variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10" onClick={() => del(u.id)}><Trash2 size={13} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UserDialog edit={edit} setEdit={setEdit} save={save} />
    </div>
  );
}
function UserDialog({ edit, setEdit, save }) {
  const [f, setF] = useState(edit);
  useEffect(() => setF(edit), [edit]);
  if (!f) return null;
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="bg-card">
        <DialogHeader><DialogTitle>{f.id ? "Edit" : "Add"} User</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" data-testid="user-name-input" /></div>
          <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" data-testid="user-email-input" /></div>
          <div><Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["super_admin", "owner", "partner", "manager", "staff"].map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Password {f.id && <span className="text-muted-foreground">(leave blank to keep)</span>}</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1.5" data-testid="user-password-input" /></div>
          <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /><Label>Active</Label></div>
        </div>
        <DialogFooter><Button onClick={() => save(f)} className="bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="save-user-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Refunds ---------- */
function Refunds() {
  const [rows, setRows] = useState(null);
  const [oid, setOid] = useState("");
  const [reason, setReason] = useState("");
  const load = useCallback(() => api.get("/admin/refunds").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loading />;
  const create = async () => { try { await api.post("/admin/refunds", { order_id: oid, reason }); toast.success("Refund recorded"); setOid(""); setReason(""); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <Head title="Refunds" sub="Admin Only" />
      <div className="mb-6 rounded-2xl bg-card p-5 gold-border">
        <div className="grid gap-3 sm:grid-cols-3">
          <Input placeholder="Order ID" value={oid} onChange={(e) => setOid(e.target.value)} data-testid="refund-order-input" />
          <Input placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="refund-reason-input" />
          <Button onClick={create} className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="create-refund-btn"><RotateCcw size={15} /> Record Refund</Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Find the Order ID from the Orders & Search tab. Refunds update reporting; gateway refund is handled separately.</p>
      </div>
      <div className="rounded-2xl bg-card p-2 gold-border">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-3">Date</th><th>Order</th><th>Amount</th><th>Reason</th><th>Status</th><th>By</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/50">
                <td className="p-3 text-xs">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                <td className="font-mono text-xs">{r.order_id.slice(0, 10)}</td>
                <td className="gold-text font-semibold">{fmt(r.amount)}</td><td className="text-xs">{r.reason}</td>
                <td><span className="text-xs text-amber-400">{r.status}</span></td><td className="text-xs">{r.requested_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Audit ---------- */
function Audit() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setRows(r.data)); }, []);
  if (!rows) return <Loading />;
  return (
    <div>
      <Head title="Audit Log" sub="Accountability" />
      <div className="overflow-x-auto rounded-2xl bg-card p-2 gold-border">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-3">Time</th><th>User</th><th>Role</th><th>Action</th><th>Entity</th><th>IP</th></tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className="border-b border-border/50">
                <td className="p-3 whitespace-nowrap text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString("en-IN")}</td>
                <td className="text-xs">{l.user_name}</td><td className="text-xs">{l.user_role}</td>
                <td><span className="rounded bg-gold-muted px-2 py-0.5 text-xs gold-text">{l.action}</span></td>
                <td className="text-xs">{l.entity_type || "—"}</td><td className="text-xs text-muted-foreground">{l.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Export ---------- */
function Export() {
  const [range, setRange] = useState("all");
  const reports = [["sessions", "Sessions"], ["cash", "Cash Transactions"], ["upi", "UPI Transactions"], ["settlement", "Partner Settlement"], ["chairs", "Chair Performance"], ["locations", "Location Performance"]];
  const dl = (report, format) => {
    const token = localStorage.getItem("kps_token");
    const url = `${API}/export/${report}?format=${format}&range=${range}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `kps_${report}_${range}.${format === "excel" ? "xlsx" : "csv"}`; a.click(); })
      .catch(() => toast.error("Export failed"));
  };
  return (
    <div>
      <Head title="Report Export" sub="CSV & Excel">
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-40" data-testid="export-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem><SelectItem value="yesterday">Yesterday</SelectItem>
            <SelectItem value="week">This Week</SelectItem><SelectItem value="month">This Month</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </Head>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {reports.map(([k, label]) => (
          <div key={k} className="rounded-2xl bg-card p-5 gold-border">
            <div className="font-serif text-lg font-semibold">{label}</div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" className="flex-1 gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" onClick={() => dl(k, "csv")} data-testid={`export-${k}-csv`}><Download size={14} /> CSV</Button>
              <Button size="sm" variant="outline" className="flex-1 gap-1.5 gold-border text-gold" onClick={() => dl(k, "excel")} data-testid={`export-${k}-excel`}><Download size={14} /> Excel</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Settings ---------- */
function SettingsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Loading />;
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => { try { await api.put("/admin/settings", { data: s }); toast.success("Settings saved"); } catch (e) { toast.error(apiError(e)); } };
  const F = ({ k, label, type = "text" }) => (
    <div><Label>{label}</Label><Input type={type} value={s[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} className="mt-1.5" data-testid={`setting-${k}`} /></div>
  );
  return (
    <div>
      <Head title="Business Settings" sub="Configuration">
        <Button onClick={save} className="bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="save-settings-btn">Save Changes</Button>
      </Head>
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Business">
          <F k="business_name" label="Business Name" /><F k="logo_text" label="Logo Text" />
          <F k="tagline" label="Tagline" /><F k="phone" label="Phone" />
          <F k="email" label="Email" /><F k="address" label="Address" />
          <F k="receipt_footer" label="Receipt Footer" />
        </Section>
        <Section title="Payment (UPI)">
          <F k="upi_id" label="UPI ID" /><F k="upi_name" label="UPI Display Name" />
          <F k="currency_symbol" label="Currency Symbol" /><F k="tax_percent" label="Tax %" type="number" />
          <div className="flex items-center gap-2"><Switch checked={s.enable_cash} onCheckedChange={(v) => set("enable_cash", v)} /><Label>Enable Cash</Label></div>
          <div className="flex items-center gap-2"><Switch checked={s.enable_upi} onCheckedChange={(v) => set("enable_upi", v)} /><Label>Enable UPI</Label></div>
          <div className="flex items-center gap-2"><Switch checked={s.enable_online} onCheckedChange={(v) => set("enable_online", v)} /><Label>Enable Online (Razorpay)</Label></div>
        </Section>
        <Section title="Partnership Split">
          <F k="partner_a_name" label="My Name (Owner)" /><F k="partner_a_pct" label="My Share % (Owner)" type="number" />
          <F k="partner_b_name" label="Partner Name" /><F k="partner_b_pct" label="Partner Share %" type="number" />
        </Section>
        <Section title="Operations">
          <F k="operating_open" label="Opening Time" /><F k="operating_close" label="Closing Time" />
          <F k="operating_hours_per_day" label="Hours / Day" type="number" />
          <F k="billing_prefix" label="Billing Code Prefix" /><F k="session_expiry_minutes" label="Billing Code Expiry (min)" type="number" />
        </Section>
        <Section title="First Visit Special">
          <p className="text-xs text-muted-foreground">Auto-applies for a mobile number that has never had a paid session before.</p>
          <div className="flex items-center gap-2"><Switch checked={s.first_time_offer_enabled} onCheckedChange={(v) => set("first_time_offer_enabled", v)} data-testid="setting-first_time_offer_enabled" /><Label>Enable First-Visit Offer</Label></div>
          <F k="first_time_offer_label" label="Offer Label" />
          <F k="first_time_offer_minutes" label="Duration (minutes)" type="number" />
          <F k="first_time_offer_price" label="Offer Price (₹)" type="number" />
        </Section>
        <Section title="Eye Massager Add-On">
          <p className="text-xs text-muted-foreground">Optional paid upgrade offered on eligible massage durations. Disabling it hides the option going forward without affecting past transactions.</p>
          <div className="flex items-center gap-2"><Switch checked={s.eye_massager_enabled} onCheckedChange={(v) => set("eye_massager_enabled", v)} data-testid="setting-eye_massager_enabled" /><Label>Enable Eye Massager Add-On</Label></div>
          <F k="eye_massager_name" label="Add-On Name" />
          <F k="eye_massager_price" label="Add-On Price (₹)" type="number" />
          <div>
            <Label>Available Durations</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {[10, 20, 30].map((d) => {
                const list = s.eye_massager_durations || [];
                const on = list.includes(d);
                return (
                  <button key={d} type="button" data-testid={`eye-duration-${d}`}
                    onClick={() => set("eye_massager_durations", on ? list.filter((x) => x !== d) : [...list, d].sort((a, b) => a - b))}
                    className={`rounded-lg border px-3 py-1.5 text-sm ${on ? "border-emerald-400 bg-emerald-500/10 text-emerald-300" : "border-border text-muted-foreground"}`}>
                    {d} min {on ? "✓" : ""}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">10 min is not recommended for the add-on.</p>
          </div>
        </Section>
      </div>
    </div>
  );
}
function Section({ title, children }) {
  return <div className="rounded-2xl bg-card p-6 gold-border"><div className="mb-4 font-serif text-lg font-semibold">{title}</div><div className="space-y-3">{children}</div></div>;
}

/* ---------- shared ---------- */
function StatusBadge({ status }) {
  const map = { available: "bg-emerald-500/15 text-emerald-400", active: "bg-gold-muted text-gold", out_of_service: "bg-destructive/15 text-destructive", maintenance: "bg-amber-500/15 text-amber-400" };
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[status] || "bg-secondary"}`}>{status.replace("_", " ")}</span>;
}
function Mini({ l, v }) { return <div className="rounded-lg bg-secondary/40 p-2"><div className="text-[10px] text-muted-foreground">{l}</div><div className="font-semibold">{v}</div></div>; }
function FormDialog({ edit, setEdit, title, save, fields, toggle }) {
  const [f, setF] = useState(edit);
  useEffect(() => setF(edit), [edit]);
  if (!f) return null;
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="bg-card">
        <DialogHeader><DialogTitle>{f.id ? "Edit" : "Add"} {title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map(([k, label, type]) => (
            <div key={k}><Label>{label}</Label><Input type={type || "text"} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5" data-testid={`field-${k}`} /></div>
          ))}
          {toggle && <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /><Label>Active</Label></div>}
        </div>
        <DialogFooter><Button onClick={() => save(f)} className="bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="form-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
